import type { BillingPlan } from '../index'
import { getChargebeeProductId } from './get-chargebee-product-id'
import { getFeatures } from '../get-features'
import type { OptionsDocument, FeatureOptions } from 'paintscout'
import moment from 'moment'
import deepmerge from 'deepmerge'

export function getPlans(args?: { deprecated?: boolean; options?: OptionsDocument }): BillingPlan[] {
  const trialFeatures = getFeatures({ options: args?.options })

  const premiumChanges: FeatureOptions = {
    reports: {
      enabled: false
    },
    presentation: {
      enabled: false
    },
    quotes: {
      conversations: { enabled: false },
      followUps: { enabled: false }
    },
    team: {
      enabled: true
    },
    gamification: {
      enabled: true,
      features: {
        enableArchiveSearch: false,
        dashboardStandings: true
      }
    }
  }
  const premiumFeatures = deepmerge(trialFeatures, premiumChanges)

  const businessChanges: FeatureOptions = {
    reports: {
      enabled: true
    },
    presentation: {
      enabled: true
    },
    quotes: {
      conversations: { enabled: true },
      followUps: { enabled: true }
    }
  }
  const businessFeatures = deepmerge(premiumFeatures, businessChanges)

  const crmEarlyBirdActive = moment().isBefore(moment('2025-12-31'))

  const coreMarketingFeatures = [
    'Professional and accurate estimates that sell more',
    'A polished sales process that runs smoothly',
    'Creating efficiency & accountability for your team',
    'Structure for your customer communication & follow-ups',
    'Access to sales insights & key analytics'
  ]

  const crmMarketingFeatures = [
    'To manage your customer info and keep your team in the loop',
    'Visibility into every deal and client',
    'A calendar view and scheduling to keep your team on track'
  ]

  const plans = [
    { value: 'paintscout-202009-addon-user-monthly', price: '1900', label: 'Additional User' } as any as BillingPlan,
    { value: 'trial', label: 'Trial', features: trialFeatures } as any as BillingPlan,
    {
      value: 'paintscout-202103-premium-annual',
      label: 'Premium - Annual',
      sublabel: 'per user, per month',
      price: '$59',
      disclaimer: 'Billed annually',
      features: premiumFeatures,
      addons: {
        crewUser: 'paintscout-202009-addon-user-annual'
      },
      deprecated: true
    },
    {
      value: 'paintscout-202103-premium-monthly',
      label: 'Premium - Monthly',
      sublabel: 'per user, per month',
      disclaimer: 'Billed monthly',
      price: '$79',
      features: premiumFeatures,
      addons: {
        crewUser: 'paintscout-202009-addon-user-monthly'
      },
      deprecated: true
    },
    {
      value: 'paintscout-202103-business-annual',
      sublabel: 'per user, per month',
      label: 'Business - Annual',
      disclaimer: 'Billed annually',
      price: '$79',
      features: businessFeatures,
      addons: {
        crewUser: 'paintscout-202009-addon-user-annual'
      },
      deprecated: true
    },
    {
      value: 'paintscout-202103-business-monthly',
      sublabel: 'per user, per month',
      label: 'Business - Monthly',
      disclaimer: 'Billed monthly',
      price: '$99',
      features: businessFeatures,
      addons: {
        crewUser: 'paintscout-202009-addon-user-monthly'
      },
      deprecated: true
    },
    {
      value: 'paintscout_202404_annual',
      label: 'Business - Annual',
      disclaimer: 'Billed annually',
      price: '$99',
      features: businessFeatures,
      billingUnit: '/user/mo',
      addons: {
        crewUser: 'paintscout-202009-addon-user-annual'
      },
      marketingFeatures: coreMarketingFeatures,
      deprecated: true
    },
    {
      value: 'paintscout_202404_monthly',
      label: 'Business - Monthly',
      disclaimer: 'Billed monthly',
      price: '$119',
      features: businessFeatures,
      billingUnit: '/user/mo',
      addons: {
        crewUser: 'paintscout-202009-addon-user-monthly'
      },
      marketingFeatures: coreMarketingFeatures,
      deprecated: true
    },
    {
      value: getChargebeeProductId('sales-monthly'),
      sublabel: 'Core Platform',
      label: 'Sales',
      disclaimer: 'Billed Monthly\nAnnual Price $99 user/mo',
      price: '$119',
      features: businessFeatures,
      billingUnit: '/user/mo',
      addons: {},
      marketingFeatures: coreMarketingFeatures
    },
    {
      value: getChargebeeProductId('sales-yearly'),
      sublabel: 'Core Platform',
      label: 'Sales',
      disclaimer: 'Billed Annually\nMonthly Price $119 user/mo',
      price: '$99',
      features: businessFeatures,
      billingUnit: '/user/mo',
      addons: {},
      marketingFeatures: coreMarketingFeatures
    },

    {
      value: getChargebeeProductId('sales-monthly'),
      sublabel: 'Add-On',
      label: 'CRM',
      disclaimer: `Billed Monthly\n${crmEarlyBirdActive ? 'Regular Price +$49 user/mo' : '\n'}`,
      price: crmEarlyBirdActive ? '$39' : '$49',
      features: businessFeatures,
      billingUnit: '/user/mo',
      addons: {
        crm: getChargebeeProductId('crm-monthly')
      },
      marketingFeatures: crmMarketingFeatures,
      discount: crmEarlyBirdActive
        ? {
            originalPrice: '$49',
            label: 'EARLY BIRD PRICING'
          }
        : undefined
    },
    {
      value: getChargebeeProductId('sales-yearly'),
      sublabel: 'Add-On',
      label: 'CRM',
      disclaimer: `Billed Annually\n${crmEarlyBirdActive ? 'Regular Price +$42 user/mo' : '\n'}`,
      price: crmEarlyBirdActive ? '$33' : '$42',
      features: businessFeatures,
      billingUnit: '/user/mo',
      addons: {
        crm: getChargebeeProductId('crm-yearly')
      },
      marketingFeatures: crmMarketingFeatures,
      discount: crmEarlyBirdActive
        ? {
            originalPrice: '$42',
            label: 'EARLY BIRD PRICING'
          }
        : undefined
    }
  ]

  return plans.filter((plan) => {
    if (!(args?.deprecated ?? true) && plan.deprecated) {
      return false
    }

    return true
  })
}
